import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Course, CourseContainer } from '../types';
import courseContainerService from '../services/courseContainerService';
import videoContainerService, { VideoContainer } from '../services/videoContainerService';
import courseService from '../services/course.service';

interface CourseCopyDialogProps {
  course: Course | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CourseCopyDialog: React.FC<CourseCopyDialogProps> = ({ course, open, onClose, onSuccess }) => {
  const [courseContainers, setCourseContainers] = useState<CourseContainer[]>([]);
  const [videoContainers, setVideoContainers] = useState<VideoContainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedContainer, setSelectedContainer] = useState<number | null>(null);
  const [copyQuickRecapCourse, setCopyQuickRecapCourse] = useState(false);
  const [quickRecapCourseContainer, setQuickRecapCourseContainer] = useState<number | null>(null);
  const [copyQuickRecapVideo, setCopyQuickRecapVideo] = useState(false);
  const [quickRecapVideoContainer, setQuickRecapVideoContainer] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      loadContainers();
    }
  }, [open]);

  const loadContainers = async () => {
    try {
      const [courses, videos] = await Promise.all([
        courseContainerService.getContainers(),
        videoContainerService.getContainers(),
      ]);
      setCourseContainers(courses);
      setVideoContainers(videos);
    } catch (err) {
      console.error('Error loading containers:', err);
      setError('Erreur lors du chargement des dossiers');
    }
  };

  const handleCopy = async () => {
    if (!course) return;

    try {
      setLoading(true);
      setError(null);

      await courseService.duplicateCourse(course.id, {
        container: selectedContainer,
        copy_quick_recap_course: copyQuickRecapCourse,
        quick_recap_course_container: copyQuickRecapCourse ? quickRecapCourseContainer : null,
        copy_quick_recap_video: copyQuickRecapVideo,
        quick_recap_video_container: copyQuickRecapVideo ? quickRecapVideoContainer : null,
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error copying course:', err);
      setError('Erreur lors de la copie du cours');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedContainer(null);
    setCopyQuickRecapCourse(false);
    setQuickRecapCourseContainer(null);
    setCopyQuickRecapVideo(false);
    setQuickRecapVideoContainer(null);
    setError(null);
    onClose();
  };

  if (!course) return null;

  const hasQuickRecapCourse = !!course.quick_recap_course_title;
  const hasQuickRecapVideo = !!course.quick_recap_video_title;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copier le cours vers mon atelier</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Cours : <strong>{course.title}</strong>
          </Typography>

          <Divider />

          {/* Main course container */}
          <FormControl fullWidth>
            <InputLabel>Dossier de destination</InputLabel>
            <Select
              value={selectedContainer || ''}
              onChange={(e) => setSelectedContainer(e.target.value ? Number(e.target.value) : null)}
              label="Dossier de destination"
            >
              <MenuItem value="">
                <em>Racine (aucun dossier)</em>
              </MenuItem>
              {courseContainers.map((container) => (
                <MenuItem key={container.id} value={container.id}>
                  {container.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quick recap options */}
          {(hasQuickRecapCourse || hasQuickRecapVideo) && (
            <>
              <Divider />
              <Typography variant="subtitle2">Éléments de référence (Quick Recap)</Typography>

              {hasQuickRecapCourse && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={copyQuickRecapCourse}
                        onChange={(e) => setCopyQuickRecapCourse(e.target.checked)}
                      />
                    }
                    label={`Copier le cours : ${course.quick_recap_course_title}`}
                  />

                  {copyQuickRecapCourse && (
                    <FormControl fullWidth sx={{ mt: 1, ml: 4 }}>
                      <InputLabel>Dossier pour le cours Quick Recap</InputLabel>
                      <Select
                        value={quickRecapCourseContainer || ''}
                        onChange={(e) => setQuickRecapCourseContainer(e.target.value ? Number(e.target.value) : null)}
                        label="Dossier pour le cours Quick Recap"
                      >
                        <MenuItem value="">
                          <em>Racine (aucun dossier)</em>
                        </MenuItem>
                        {courseContainers.map((container) => (
                          <MenuItem key={container.id} value={container.id}>
                            {container.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}

              {hasQuickRecapVideo && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={copyQuickRecapVideo}
                        onChange={(e) => setCopyQuickRecapVideo(e.target.checked)}
                      />
                    }
                    label={`Copier la vidéo : ${course.quick_recap_video_title}`}
                  />

                  {copyQuickRecapVideo && (
                    <FormControl fullWidth sx={{ mt: 1, ml: 4 }}>
                      <InputLabel>Dossier pour la vidéo Quick Recap</InputLabel>
                      <Select
                        value={quickRecapVideoContainer || ''}
                        onChange={(e) => setQuickRecapVideoContainer(e.target.value ? Number(e.target.value) : null)}
                        label="Dossier pour la vidéo Quick Recap"
                      >
                        <MenuItem value="">
                          <em>Racine (aucun dossier)</em>
                        </MenuItem>
                        {videoContainers.map((container) => (
                          <MenuItem key={container.id} value={container.id}>
                            {container.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Copie en cours...' : 'Copier'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseCopyDialog;
